import { Offer } from "@/services/backend/vbs/offers/types/offer";
import t from "@/lang/lang";
import { useState } from "react";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { useParams } from "react-router";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  UpdateDateRequest,
  updateVbsOfferCache,
  useVbsOfferResetReferenceNumberMutation,
  useVbsOfferShowQuery,
  useVbsOfferUpdateDateMutation,
  useVbsOfferPatchMutation,
} from "@/services/backend/vbs/offers/api/offer-api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { DatePickerField } from "@/shared/components/field/date-picker-field";
import { ReferenceNumberField } from "@/services/backend/documents/components/reference-number-field";
import { PricingCards } from "@/services/backend/vbs/pricing/components/pricing-cards";
import { Textblocks } from "@/services/backend/textblocks/components/textblocks";
import { GSMSubdomain } from "@/shared/types/gsm-subdomain";
import { PricingByAreaCard } from "@/services/backend/vbs/pricing/components/pricing-by-area-card";
import { isImmutable } from "@/services/backend/documents/lib/document-state";

export function OfferDataRoute() {
  const { offerId } = useParams();

  const {
    data: offer,
    isLoading,
    error,
  } = useVbsOfferShowQuery({
    id: offerId!,
  });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Card className="text-muted-foreground animate-pulse p-6">
        {t("Lade Daten...")}
      </Card>
    );
  }

  return <OfferView offer={offer!} />;
}

export function OfferView({ offer }: { offer: Offer }) {
  const disabled = isImmutable(offer);

  return (
    <div className="grid gap-2">
      <BaseData offer={offer} />
      <PricingCards pricingId={offer.pricingId} disabled={disabled} />
      <PricingByAreaCard
        pricingByAreaId={offer.pricingByAreaId}
        disabled={disabled}
      />
      <Textblocks
        subdomain={GSMSubdomain.VBS}
        documentKind={offer!.documentKind}
        documentId={offer!.id}
        title={t("Bemerkungen im Angebot")}
        defaultOpen={false}
      />
    </div>
  );
}

function BaseData({ offer }: { offer: Offer }) {
  const patchReferenceNumber = useVbsOfferPatchMutation();
  const resetReferenceNumber = useVbsOfferResetReferenceNumberMutation();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Angebot")}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 lg:grid-cols-2">
          <ReferenceNumberField
            data={offer}
            updateCache={updateVbsOfferCache}
            patchMutation={patchReferenceNumber}
            resetMutation={resetReferenceNumber}
            disabled={isImmutable(offer)}
          />
          <DateView offer={offer} />
        </div>
      </CardContent>
    </Card>
  );
}

function DateView(props: { offer: Offer }) {
  const { offer } = props;
  const [request, setRequest] = useState<UpdateDateRequest>({
    id: offer.id,
    date: offer.offerDate,
  });
  const mutation = useVbsOfferUpdateDateMutation();
  const { isLoading } = useDebouncedMutation(
    request,
    setRequest,
    mutation,
    {
      id: offer.id,
      date: offer.offerDate,
    },
    { delay: 0 },
  );

  return (
    <DatePickerField
      label={t("Angebotsdatum")}
      nullable
      date={request.date || undefined}
      onDateChange={(date) => setRequest({ ...request, date: date ?? "" })}
      fieldName="date"
      disabled={isLoading || isImmutable(offer)}
    />
  );
}
